// Pre-trade preview math.
//
// Every number here comes out of `@ostium/formulae` — the same package the
// contracts and the Ostium frontend use. This module only marshals raw
// subgraph strings into the shapes those functions expect and formats the
// results back to human-readable numbers. Do not add trading math here; add
// it to `@ostium/formulae` and call it from here.
//
// Ported from the frontend's `stores/tradeForm.tsx` + `utils/calculations.ts`,
// so a builder's order ticket agrees with what ostium.io shows.

import {
  CalculateDynamicPriceImpact,
  GetEffectiveMaxLeverage,
  GetOpeningFee,
  GetTradeLiquidationPrice,
  TimeUntilZeroDynamicSpread,
  WithinExposureLimit,
} from '@ostium/formulae';
import {
  formatLeverage,
  formatTokens,
  formatUsdc,
  parseLeverage,
  parseTokens,
  parseUsdc,
  timeElapsedSince,
} from './decimal.js';
import { normalizePairName } from './pair-names.js';
import { MAX_COLLATERAL_USD, MIN_OPEN_SIZE_USD } from '../../internal/precision.js';
import type { RawPair, RawVault } from './raw.js';
import type {
  PriceData,
  PreviewOpenTradeResult,
  PreviewWarning,
} from '../types.js';

/** 1e12 — scales an 18-decimal token notional down to 6-decimal USDC. */
const PRECISION_12 = 10n ** 12n;
const PRECISION_18 = 10n ** 18n;
/** Leverage carries 2 decimals on-chain. */
const PRECISION_2 = 100n;

export interface PreviewInput {
  raw: RawPair;
  price: PriceData;
  isLong: boolean;
  /** Gross collateral in USDC the trader is putting up. */
  collateral: number;
  leverage: number;
  /** Limit/stop price. When set, it is the reference price instead of bid/ask. */
  limitPrice?: number;
  isDayTrade?: boolean;
  /** Vault balance in USDC. Omitted → the exposure-limit check is skipped. */
  vaultBalance?: number;
  /**
   * Builder fee in basis points, as configured on the client. Charged on
   * notional at open, so a builder-integrated ticket that omits it understates
   * fees and overstates everything derived from collateral.
   */
  builderFeeBps?: number;
}

/** Side open-interest valued in USDC (6 decimals), as `WithinExposureLimit` expects. */
function oiUsdc(oiTokens: string, price: number): bigint {
  return (BigInt(oiTokens) * parseTokens(price)) / PRECISION_18 / PRECISION_12;
}

/**
 * Blended open-fee percentage. When the order straddles the maker/taker
 * boundary the two legs are weighted by their notionals; with no size yet we
 * fall back to whichever side the trade would land on.
 */
function blendedOpenFeePercent(
  takerAbs: number,
  makerAbs: number,
  takerPercent: number,
  makerPercent: number,
  oiLong: bigint,
  oiShort: bigint,
  isLong: boolean,
): number {
  if (takerAbs + makerAbs > 0) {
    return (makerPercent * makerAbs + takerPercent * takerAbs) / (makerAbs + takerAbs);
  }

  const delta = formatUsdc(oiLong.toString()) - formatUsdc(oiShort.toString());
  if (delta === 0) return takerPercent;

  // Trading against the imbalance makes you the maker.
  const isMaker = (delta > 0 && !isLong) || (delta < 0 && isLong);
  return isMaker ? makerPercent : takerPercent;
}

/** Vault balance in USDC from the raw vault entity. */
export function vaultBalanceUsdc(vault: RawVault): number {
  const maxAccPnlPerToken = PRECISION_18 + BigInt(vault.rewardsPerToken);
  const balance =
    ((maxAccPnlPerToken - BigInt(vault.accPnlPerTokenUsed)) * BigInt(vault.shares)) / PRECISION_18;
  return formatUsdc(balance.toString());
}

/**
 * Price the order would execute at, plus the dynamic spread it pays.
 *
 * Market orders start from the side's bid/ask and pay the imbalance spread
 * returned by `CalculateDynamicPriceImpact`. Limit orders execute at their
 * trigger price, so they show no impact.
 */
function previewExecution(
  raw: RawPair,
  price: PriceData,
  isLong: boolean,
  collateral: number,
  leverage: number,
  limitPrice?: number,
): { refPx: number; entryPx: number; priceImpactP: number; spreadDecaySeconds: number } {
  const refPx = limitPrice ?? (isLong ? price.ask : price.bid);

  if (limitPrice !== undefined || !collateral || !leverage) {
    return { refPx, entryPx: refPx, priceImpactP: 0, spreadDecaySeconds: 0 };
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  const lastUpdate = parseInt(raw.lastUpdateTimestamp ?? '0') || 0;
  const buyVolume = raw.buyVolume ?? '0';
  const sellVolume = raw.sellVolume ?? '0';
  const decayRate = raw.decayRate ?? '0';
  const netVolThreshold = raw.netVolThreshold ?? '0';

  const impact = CalculateDynamicPriceImpact(
    parseTokens(price.mid).toString(),
    parseTokens(price.ask).toString(),
    parseTokens(price.bid).toString(),
    true, // isOpen
    isLong,
    parseUsdc(collateral).toString(),
    parseLeverage(leverage).toString(),
    buyVolume,
    sellVolume,
    decayRate,
    timeElapsedSince(raw),
    netVolThreshold,
    raw.priceImpactK ?? '0',
  );

  const decay = TimeUntilZeroDynamicSpread(
    buyVolume,
    sellVolume,
    lastUpdate.toString(),
    netVolThreshold,
    decayRate,
    nowSeconds.toString(),
    isLong,
    true, // isOpen
  );

  return {
    refPx,
    entryPx: formatTokens(impact.priceAfterImpact) || refPx,
    priceImpactP: formatTokens(impact.priceImpactP),
    spreadDecaySeconds: parseInt(decay) || 0,
  };
}

/**
 * Everything an order ticket needs to render before a trade is signed:
 * execution price, fees, resulting liquidation price, and the validity checks
 * that would otherwise revert on-chain.
 */
export function previewOpenTrade(input: PreviewInput): PreviewOpenTradeResult {
  const { raw, price, isLong, collateral, leverage, limitPrice, isDayTrade = false } = input;

  const { refPx, entryPx, priceImpactP, spreadDecaySeconds } = previewExecution(
    raw,
    price,
    isLong,
    collateral,
    leverage,
    limitPrice,
  );

  // ── Fees ───────────────────────────────────────────────────────────────
  const safeCollateral = Number.isFinite(collateral) ? Number((collateral || 0).toFixed(6)) : 0;
  const notionalUsdc = (parseUsdc(safeCollateral) * parseLeverage(leverage)) / PRECISION_2;

  const oiLong = oiUsdc(raw.longOI, refPx);
  const oiShort = oiUsdc(raw.shortOI, refPx);

  const opening = GetOpeningFee(
    oiLong.toString(),
    oiShort.toString(),
    raw.makerFeeP ?? '0',
    raw.takerFeeP ?? '0',
    raw.makerMaxLeverage ?? '0',
    (isLong ? notionalUsdc : -notionalUsdc).toString(),
    parseLeverage(leverage).toString(),
  );

  const takerFeePercent = raw.takerFeeP ? formatUsdc(raw.takerFeeP) : 0;
  const makerFeePercent = raw.makerFeeP ? formatUsdc(raw.makerFeeP) : 0;
  const takerNotional = formatUsdc(opening?.takerAmount ?? '0');
  const makerNotional = formatUsdc(opening?.makerAmount ?? '0');

  const openFee = formatUsdc(opening?.baseFee ?? '0');
  const oracleFee = raw.fee?.oracleFee ? formatUsdc(raw.fee.oracleFee) : 0;
  // Builder fee is bps of notional, charged alongside the protocol open fee.
  const builderFeeBps = input.builderFeeBps ?? 0;
  const notionalUsd = formatUsdc(notionalUsdc.toString());
  const builderFee = builderFeeBps > 0 ? (notionalUsd * builderFeeBps) / 10_000 : 0;
  const totalFees = openFee + oracleFee + builderFee;

  // ── Sizing ─────────────────────────────────────────────────────────────
  const collateralAtOpen = safeCollateral - totalFees;
  const exposure = collateralAtOpen * leverage;
  const positionSize = entryPx > 0 ? exposure / entryPx : 0;

  // ── Leverage + liquidation ─────────────────────────────────────────────
  // `'100'` is 1x: leverage carries 2 decimals on-chain, so a `'1'` fallback
  // would mean 0.01x and reject every trade with "exceeds the max 0.01x".
  const pairMaxLeverage =
    raw.maxLeverage && raw.maxLeverage !== '0' ? raw.maxLeverage : raw.group?.maxLeverage ?? '100';
  const effectiveMaxLeverageRaw = GetEffectiveMaxLeverage(
    pairMaxLeverage,
    raw.overnightMaxLeverage ?? '0',
    isDayTrade,
  );
  const effectiveMaxLeverage = formatLeverage(effectiveMaxLeverageRaw);
  const minLeverage = raw.group?.minLeverage ? formatLeverage(raw.group.minLeverage) : 1;

  // Liquidation is quoted off the execution price, which is what the contract
  // records as the trade's open price — rollover and funding start at zero.
  //
  // Collateral here is the GROSS amount, not `collateralAtOpen`. That looks
  // inconsistent with the sizing above, and it is what the frontend does
  // (`tradeForm.tsx` passes the same gross `collateral`), so the two agree.
  // Do not "fix" this to `collateralAtOpen` without changing the frontend too.
  let liquidationPx = 0;
  if (entryPx > 0 && safeCollateral > 0 && leverage > 0) {
    const liq = formatTokens(
      GetTradeLiquidationPrice(
        parseTokens(entryPx).toString(),
        isLong,
        parseUsdc(safeCollateral).toString(),
        parseLeverage(leverage).toString(),
        '0', // rollover — a new trade has accrued none
        '0', // funding
        effectiveMaxLeverageRaw,
      ),
    );
    liquidationPx = liq > 0 ? liq : 0;
  }

  // ── Validity ───────────────────────────────────────────────────────────
  const minPositionSize = raw.fee?.minLevPos ? formatUsdc(raw.fee.minLevPos) : 0;

  let withinExposureLimit: boolean | undefined;
  if (input.vaultBalance !== undefined && raw.group?.maxCollateralP) {
    withinExposureLimit = WithinExposureLimit(
      (isLong ? oiLong : oiShort).toString(),
      raw.maxOI,
      (isLong ? raw.group.longCollateral : raw.group.shortCollateral) ?? '0',
      raw.group.maxCollateralP,
      parseUsdc(input.vaultBalance).toString(),
      parseUsdc(safeCollateral.toFixed(6)).toString(),
      parseLeverage(leverage).toString(),
    );
  }

  const warnings: PreviewWarning[] = [];

  // Checked first, and not gated on a positive collateral — the guards below
  // are (`FEES_EXCEED_COLLATERAL` needs `> 0`, `BELOW_MIN_POSITION_SIZE` needs
  // a positive exposure), so without this a zero-collateral preview — an empty
  // order-ticket field — came back `isValid: true` with a negative exposure.
  if (safeCollateral < MIN_OPEN_SIZE_USD) {
    warnings.push({
      code: 'BELOW_MIN_COLLATERAL',
      message: `Collateral $${safeCollateral} is below the $${MIN_OPEN_SIZE_USD} minimum to open a trade.`,
    });
  }
  if (safeCollateral > MAX_COLLATERAL_USD) {
    warnings.push({
      code: 'ABOVE_MAX_COLLATERAL',
      message: `Collateral $${safeCollateral} exceeds the $${MAX_COLLATERAL_USD} maximum for a single trade.`,
    });
  }

  if (leverage > effectiveMaxLeverage) {
    warnings.push({
      code: 'LEVERAGE_ABOVE_MAX',
      message: `Leverage ${leverage}x exceeds the max ${effectiveMaxLeverage}x for this pair${
        isDayTrade ? '' : ' (open a day trade for a higher cap)'
      }.`,
    });
  }
  if (leverage > 0 && leverage < minLeverage) {
    warnings.push({
      code: 'LEVERAGE_BELOW_MIN',
      message: `Leverage ${leverage}x is below the minimum ${minLeverage}x for this pair.`,
    });
  }
  if (minPositionSize > 0 && exposure > 0 && exposure < minPositionSize) {
    warnings.push({
      code: 'BELOW_MIN_POSITION_SIZE',
      message: `Position size $${exposure.toFixed(2)} is below the $${minPositionSize} minimum for this pair.`,
    });
  }
  if (collateralAtOpen <= 0 && safeCollateral > 0) {
    warnings.push({
      code: 'FEES_EXCEED_COLLATERAL',
      message: `Fees ($${totalFees.toFixed(2)}) meet or exceed the collateral provided.`,
    });
  }
  if (withinExposureLimit === false) {
    warnings.push({
      code: 'ABOVE_EXPOSURE_LIMIT',
      message: 'Trade exceeds the open-interest or group-collateral limit for this pair.',
    });
  }
  if (price.isMarketOpen === false) {
    warnings.push({ code: 'MARKET_CLOSED', message: 'This market is currently closed.' });
  }
  if (isDayTrade && price.isDayTradingClosed === true) {
    warnings.push({
      code: 'DAY_TRADING_CLOSED',
      message: 'Day trading is currently closed for this pair, so a day trade cannot be opened.',
    });
  }

  return {
    pairId: String(raw.id),
    pairFrom: normalizePairName(raw.from),
    pairTo: normalizePairName(raw.to),
    isLong,
    midPx: String(price.mid),
    refPx: String(refPx),
    entryPx: String(entryPx),
    priceImpactP: String(priceImpactP),
    spreadDecaySeconds,
    fees: {
      openFee: String(openFee),
      oracleFee: String(oracleFee),
      builderFee: String(builderFee),
      builderFeeBps: String(builderFeeBps),
      total: String(totalFees),
      openFeePercent: String(
        blendedOpenFeePercent(
          takerNotional,
          makerNotional,
          takerFeePercent,
          makerFeePercent,
          oiLong,
          oiShort,
          isLong,
        ),
      ),
      takerFeePercent: String(takerFeePercent),
      makerFeePercent: String(makerFeePercent),
      takerNotional: String(takerNotional),
      makerNotional: String(makerNotional),
    },
    collateral: String(safeCollateral),
    collateralAtOpen: String(collateralAtOpen),
    exposure: String(exposure),
    positionSize: String(positionSize),
    leverage: String(leverage),
    liquidationPx: String(liquidationPx),
    effectiveMaxLeverage: String(effectiveMaxLeverage),
    minLeverage: String(minLeverage),
    minPositionSize: String(minPositionSize),
    isDayTrade,
    withinExposureLimit,
    isValid: warnings.length === 0,
    warnings,
  };
}
